/**
 * Hook de estado en tiempo real del payment.
 *
 * Estrategia: abre un `EventSource` contra `GET /payments/{id}/events` (SSE) y
 * recibe un evento `snapshot` al conectar y en cada transición. Si el SSE no
 * está disponible (navegador sin EventSource, proxy que lo bloquea, o no llega
 * nada en `SSE_GRACE_MS`), cae a polling de `GET /payments/{id}`. En ambos
 * modos el stream se detiene al llegar a un estado terminal.
 */

import { useEffect, useState } from 'react';
import {
  eventsUrl,
  fetchSnapshot,
  isTerminal,
  parseSnapshot,
  type PaymentSnapshot,
} from './scrapperApi.js';

export type StreamTransport = 'connecting' | 'sse' | 'polling';

export interface PaymentStreamState {
  snapshot: PaymentSnapshot | null;
  transport: StreamTransport;
  /** El payment no existe (evento `notfound` del server o 404 en el GET). */
  notFound: boolean;
  /** Prompt de liveness en vivo del proveedor ("Centra tu cara", "Mueve la
   * cabeza"). Llega por el evento SSE `biometric_prompt`. Null si no hay. */
  biometricPrompt: string | null;
  /** Lado del documento que el scrapper pide subir ("front"/"back"), o null.
   * Llega por el evento SSE `document_request`. */
  documentRequest: string | null;
}

const POLL_INTERVAL_MS = 2000;
/** Si el SSE no entrega un snapshot en este tiempo, caemos a polling. */
const SSE_GRACE_MS = 6000;

export const usePaymentStream = (
  scrapperBase: string,
  paymentId: string,
): PaymentStreamState => {
  const [snapshot, setSnapshot] = useState<PaymentSnapshot | null>(null);
  const [transport, setTransport] = useState<StreamTransport>('connecting');
  const [notFound, setNotFound] = useState(false);
  const [biometricPrompt, setBiometricPrompt] = useState<string | null>(null);
  const [documentRequest, setDocumentRequest] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let es: EventSource | null = null;
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let graceTimer: ReturnType<typeof setTimeout> | null = null;
    let gotSnapshot = false;

    const stopAll = (): void => {
      if (es) {
        es.close();
        es = null;
      }
      if (pollTimer !== null) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
      if (graceTimer !== null) {
        clearTimeout(graceTimer);
        graceTimer = null;
      }
    };

    const apply = (snap: PaymentSnapshot | null): void => {
      if (cancelled || snap === null) return;
      gotSnapshot = true;
      setSnapshot(snap);
      if (isTerminal(snap.status)) stopAll();
    };

    const startPolling = (): void => {
      if (cancelled || pollTimer !== null) return;
      setTransport('polling');
      const tick = async (): Promise<void> => {
        const snap = await fetchSnapshot(scrapperBase, paymentId).catch(() => null);
        if (cancelled) return;
        if (snap === null && !gotSnapshot) {
          // Primera lectura nula sin haber visto nada → 404 probable.
          setNotFound(true);
          stopAll();
          return;
        }
        apply(snap);
      };
      void tick();
      pollTimer = setInterval(() => void tick(), POLL_INTERVAL_MS);
    };

    const startSse = (): void => {
      if (typeof EventSource === 'undefined') {
        startPolling();
        return;
      }
      try {
        es = new EventSource(eventsUrl(scrapperBase, paymentId));
      } catch {
        startPolling();
        return;
      }

      es.addEventListener('snapshot', (ev: MessageEvent<string>) => {
        try {
          apply(parseSnapshot(JSON.parse(ev.data)));
          if (!cancelled) setTransport('sse');
        } catch {
          /* mensaje malformado: lo ignoramos, el próximo snapshot reconcilia */
        }
      });

      es.addEventListener('notfound', () => {
        if (cancelled) return;
        setNotFound(true);
        stopAll();
      });

      // Prompt de liveness en vivo (Onfido Motion): canal aparte, alta
      // frecuencia, sin tocar la DB. Guía al usuario en tiempo real.
      es.addEventListener('biometric_prompt', (ev: MessageEvent<string>) => {
        if (cancelled) return;
        try {
          const { text } = JSON.parse(ev.data) as { text?: string };
          if (typeof text === 'string' && text) setBiometricPrompt(text);
        } catch {
          /* malformado: ignorar */
        }
      });

      // Pedido de subida de documento: side "front"/"back", o "" para cancelar.
      es.addEventListener('document_request', (ev: MessageEvent<string>) => {
        if (cancelled) return;
        try {
          const { side } = JSON.parse(ev.data) as { side?: string };
          // "" = cancelar → null (no usar `??`: '' debe mapear a null).
          setDocumentRequest(side && side.length > 0 ? side : null);
        } catch {
          /* malformado: ignorar */
        }
      });

      // El evento `error` nativo del EventSource cubre fallos de conexión; el
      // navegador reintenta solo. El graceTimer cae a polling si nunca conecta.
      graceTimer = setTimeout(() => {
        if (!gotSnapshot && !cancelled) {
          if (es) {
            es.close();
            es = null;
          }
          startPolling();
        }
      }, SSE_GRACE_MS);
    };

    startSse();

    return () => {
      cancelled = true;
      stopAll();
    };
  }, [scrapperBase, paymentId]);

  return { snapshot, transport, notFound, biometricPrompt, documentRequest };
};
